import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BackButton } from '@/components/BackButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  FileText,
  Receipt,
  ChefHat,
  Plus,
  Trash2,
  Eye,
  Download,
  Printer,
  Settings,
  Palette,
  Image,
  QrCode,
  Star,
  ArrowLeft,
  Save,
  Share2,
  Mail,
  MessageCircle,
  Copy,
  ExternalLink,
  Check,
  Sparkles
} from 'lucide-react';
import axios from 'axios';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { id: 'invoice', name: 'Factura', icon: FileText, description: 'Factura fiscal con QR AFIP', color: 'from-blue-500 to-indigo-600' },
  { id: 'ticket', name: 'Ticket / Prefactura', icon: Receipt, description: 'Cuenta para el cliente', color: 'from-emerald-500 to-teal-600' },
  { id: 'comanda', name: 'Comanda', icon: ChefHat, description: 'Pedido para cocina/bar', color: 'from-orange-500 to-red-600' }
];

const PAPER_SIZES = [
  { id: 'A4', name: 'A4 (210×297mm)', width: 210, description: 'Hoja estándar' },
  { id: '80mm', name: 'Ticket 80mm', width: 80, description: 'Impresora térmica estándar' },
  { id: '58mm', name: 'Ticket 58mm', width: 58, description: 'Impresora térmica compacta' }
];

const FONT_FAMILIES = [
  { id: 'Helvetica', name: 'Helvetica (Sans-serif)' },
  { id: 'Times-Roman', name: 'Times Roman (Serif)' },
  { id: 'Courier', name: 'Courier (Monospace)' }
];

export default function TemplateDesigner() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedType, setSelectedType] = useState('ticket');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [presets, setPresets] = useState({});
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [baseline, setBaseline] = useState(null);
  
  // Form state for editing
  const [formData, setFormData] = useState({
    name: '',
    doc_type: 'ticket',
    paper_size: '80mm',
    show_logo: true,
    show_qr: true,
    show_social: false,
    show_tip_suggestion: true,
    show_thanks_message: true,
    thanks_message: '¡Gracias por su visita!',
    primary_color: '#000000',
    accent_color: '#666666',
    font_family: 'Helvetica',
    large_text: true,
    highlight_notes: true,
    separate_by_area: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/templates/?doc_type=${selectedType}`, { headers });
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  }, [token, selectedType]);

  const fetchFields = useCallback(async (docType) => {
    try {
      const res = await axios.get(`${API}/templates/fields/${docType}`, { headers });
      setAvailableFields(res.data.fields || []);
    } catch (err) {
      console.error('Error fetching fields:', err);
    }
  }, [token]);

  const fetchPresets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/templates/presets`, { headers });
      setPresets(res.data.presets || {});
    } catch (err) {
      console.error('Error fetching presets:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
    fetchFields(selectedType);
    fetchPresets();
  }, [fetchTemplates, fetchFields, fetchPresets, selectedType]);

  const handleCreate = async () => {
    try {
      const res = await axios.post(`${API}/templates/`, {
        ...formData,
        doc_type: selectedType
      }, { headers });
      
      setTemplates([...templates, res.data]);
      setSelectedTemplate(res.data);
      setIsEditing(false);
      toast.success('Plantilla creada exitosamente');
    } catch (err) {
      toast.error('Error al crear plantilla');
    }
  };

  const handleUpdate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const res = await axios.put(
        `${API}/templates/${selectedTemplate.template_id}`,
        formData,
        { headers }
      );
      
      setTemplates(templates.map(t => 
        t.template_id === selectedTemplate.template_id ? res.data : t
      ));
      setSelectedTemplate(res.data);
      setIsEditing(false);
      toast.success('Plantilla actualizada');
    } catch (err) {
      toast.error('Error al actualizar plantilla');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    
    try {
      await axios.delete(`${API}/templates/${templateId}`, { headers });
      setTemplates(templates.filter(t => t.template_id !== templateId));
      if (selectedTemplate?.template_id === templateId) {
        setSelectedTemplate(null);
      }
      toast.success('Plantilla eliminada');
    } catch (err) {
      toast.error('Error al eliminar plantilla');
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await axios.post(`${API}/templates/${templateId}/set-default`, {}, { headers });
      setTemplates(templates.map(t => ({
        ...t,
        is_default: t.template_id === templateId
      })));
      toast.success('Plantilla predeterminada actualizada');
    } catch (err) {
      toast.error('Error al establecer predeterminada');
    }
  };

  const handlePreview = async (template) => {
    try {
      setGeneratingPreview(true);
      const res = await axios.post(
        `${API}/templates/${template.template_id}/preview`,
        {},
        { headers }
      );
      setPreviewPdf(res.data.pdf_base64);
      setShowPreview(true);
    } catch (err) {
      toast.error('Error al generar vista previa');
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!previewPdf) return;
    
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${previewPdf}`;
    link.download = `${selectedTemplate?.name || 'documento'}.pdf`;
    link.click();
    toast.success('PDF descargado');
  };

  const handlePrint = () => {
    if (!previewPdf) return;
    
    // Open PDF in new window for printing
    const pdfWindow = window.open('', '_blank');
    pdfWindow.document.write(`
      <html>
        <head><title>Imprimir - ${selectedTemplate?.name || 'Documento'}</title></head>
        <body style="margin:0">
          <embed width="100%" height="100%" src="data:application/pdf;base64,${previewPdf}" type="application/pdf" />
        </body>
      </html>
    `);
    setTimeout(() => pdfWindow.print(), 500);
  };

  const handleShareWhatsApp = () => {
    // Since we can't directly share PDF via WhatsApp without a backend file server,
    // we create a message with instructions
    const message = encodeURIComponent(
      `📄 *${selectedTemplate?.name}*\n\n` +
      `Tipo: ${DOC_TYPES.find(d => d.id === selectedTemplate?.doc_type)?.name || 'Documento'}\n` +
      `Tamaño: ${selectedTemplate?.paper_size}\n\n` +
      `Para ver el documento completo, visita nuestro local o solicítalo por email.`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
    toast.success('Abriendo WhatsApp...');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`Plantilla: ${selectedTemplate?.name}`);
    const body = encodeURIComponent(
      `Hola,\n\n` +
      `Te comparto la plantilla "${selectedTemplate?.name}" de nuestro sistema.\n\n` +
      `Tipo de documento: ${DOC_TYPES.find(d => d.id === selectedTemplate?.doc_type)?.name}\n` +
      `Tamaño de papel: ${selectedTemplate?.paper_size}\n\n` +
      `Saludos!`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.success('Abriendo cliente de correo...');
  };

  const handleCopyLink = () => {
    // Copy a deep link to this template (for internal use)
    const link = `${window.location.origin}/template-designer?template=${selectedTemplate?.template_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Enlace copiado al portapapeles');
  };

  useEffect(() => { setBaseline(isEditing ? JSON.stringify(formData) : null); }, [isEditing, selectedTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (template = null) => {
    if (template) {
      setFormData({
        name: template.name,
        doc_type: template.doc_type,
        paper_size: template.paper_size,
        show_logo: template.show_logo ?? true,
        show_qr: template.show_qr ?? true,
        show_social: template.show_social ?? false,
        show_tip_suggestion: template.show_tip_suggestion ?? true,
        show_thanks_message: template.show_thanks_message ?? true,
        thanks_message: template.thanks_message || '¡Gracias por su visita!',
        primary_color: template.primary_color || '#000000',
        accent_color: template.accent_color || '#666666',
        font_family: template.font_family || 'Helvetica',
        large_text: template.large_text ?? true,
        highlight_notes: template.highlight_notes ?? true,
        separate_by_area: template.separate_by_area ?? false
      });
      setSelectedTemplate(template);
    } else {
      setFormData({
        name: `Nueva ${DOC_TYPES.find(d => d.id === selectedType)?.name || 'Plantilla'}`,
        doc_type: selectedType,
        paper_size: selectedType === 'invoice' ? 'A4' : '80mm',
        show_logo: true,
        show_qr: true,
        show_social: false,
        show_tip_suggestion: selectedType === 'ticket',
        show_thanks_message: selectedType !== 'comanda',
        thanks_message: '¡Gracias por su visita!',
        primary_color: '#000000',
        accent_color: '#666666',
        font_family: 'Helvetica',
        large_text: selectedType === 'comanda',
        highlight_notes: selectedType === 'comanda',
        separate_by_area: false
      });
      setSelectedTemplate(null);
    }
    setIsEditing(true);
  };

  const startFromPreset = (presetKey) => {
    const preset = presets[presetKey];
    if (!preset) return;
    
    setFormData({
      name: preset.name,
      doc_type: preset.doc_type,
      paper_size: preset.paper_size,
      show_logo: true,
      show_qr: true,
      show_social: false,
      show_tip_suggestion: preset.doc_type === 'ticket',
      show_thanks_message: preset.doc_type !== 'comanda',
      thanks_message: '¡Gracias por su visita!',
      primary_color: '#000000',
      accent_color: '#666666',
      font_family: 'Helvetica',
      large_text: preset.doc_type === 'comanda',
      highlight_notes: preset.doc_type === 'comanda',
      separate_by_area: false
    });
    setSelectedTemplate(null);
    setSelectedType(preset.doc_type);
    setIsEditing(true);
    toast.success(`Usando plantilla base: ${preset.name}`);
  };

  const currentDocType = DOC_TYPES.find(d => d.id === selectedType);
  const DocTypeIcon = currentDocType?.icon || FileText;
  const dirty = isEditing && baseline !== null && JSON.stringify(formData) !== baseline;
  const closeEditor = () => { if (!dirty || window.confirm('Tenés cambios sin guardar. ¿Descartarlos?')) setIsEditing(false); };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6" data-testid="template-designer">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <BackButton dirty={dirty} className="-ml-3 mb-2" />
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentDocType?.color || 'from-violet-500 to-purple-600'} flex items-center justify-center shadow-lg`}>
              <Printer className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Diseñador de Documentos</h1>
              <p className="text-sm text-muted-foreground">Personaliza facturas, tickets y comandas</p>
            </div>
          </div>
          
          <Button onClick={() => startEdit()} className="gap-2" data-testid="new-template-btn">
            <Plus className="w-4 h-4" />
            Nueva Plantilla
          </Button>
        </div>

        {/* Document Type Tabs */}
        <Tabs value={selectedType} onValueChange={(v) => { setSelectedType(v); setSelectedTemplate(null); }} className="mb-6">
          <TabsList className="grid w-full grid-cols-3 h-auto p-1">
            {DOC_TYPES.map(type => (
              <TabsTrigger 
                key={type.id} 
                value={type.id}
                className="flex flex-col sm:flex-row items-center gap-2 py-3 data-[state=active]:shadow-md"
                data-testid={`tab-${type.id}`}
              >
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${type.color} flex items-center justify-center`}>
                  <type.icon className="w-4 h-4 text-white" />
                </div>
                <div className="text-left hidden sm:block">
                  <div className="font-medium">{type.name}</div>
                  <div className="text-xs text-muted-foreground">{type.description}</div>
                </div>
                <span className="sm:hidden text-xs">{type.name}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Templates List */}
          <div className="lg:col-span-1 space-y-4">
            {/* Existing Templates */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DocTypeIcon className="w-5 h-5" />
                  Mis Plantillas
                </CardTitle>
                <CardDescription>
                  {templates.length} {templates.length === 1 ? 'plantilla' : 'plantillas'} de {currentDocType?.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-8">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${currentDocType?.color} mx-auto mb-4 flex items-center justify-center opacity-50`}>
                      <DocTypeIcon className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-muted-foreground mb-4">No hay plantillas de {currentDocType?.name.toLowerCase()}</p>
                    <Button variant="outline" onClick={() => startEdit()}>
                      <Plus className="w-4 h-4 mr-2" />
                      Crear primera
                    </Button>
                  </div>
                ) : (
                  <ScrollArea className="h-[300px] pr-4">
                    <div className="space-y-2">
                      {templates.map(template => (
                        <div
                          key={template.template_id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                            selectedTemplate?.template_id === template.template_id
                              ? 'border-primary bg-primary/5 shadow-sm'
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => setSelectedTemplate(template)}
                          data-testid={`template-item-${template.template_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium truncate">{template.name}</span>
                                {template.is_default && (
                                  <Badge variant="secondary" className="text-xs shrink-0">
                                    <Star className="w-3 h-3 mr-1 fill-current" />
                                    Default
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {PAPER_SIZES.find(p => p.id === template.paper_size)?.name || template.paper_size}
                              </p>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => { e.stopPropagation(); handlePreview(template); }}
                                title="Vista previa"
                                disabled={generatingPreview}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => { e.stopPropagation(); startEdit(template); }}
                                title="Editar"
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Presets */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  Plantillas Predefinidas
                </CardTitle>
                <CardDescription>Comienza desde una base profesional</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(presets)
                    .filter(([_, p]) => p.doc_type === selectedType)
                    .map(([key, preset]) => (
                      <button
                        key={key}
                        onClick={() => startFromPreset(key)}
                        className="w-full p-3 rounded-lg border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all text-left"
                      >
                        <div className="font-medium text-sm">{preset.name}</div>
                        <div className="text-xs text-muted-foreground">{preset.description}</div>
                      </button>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Template Editor / Details */}
          <div className="lg:col-span-2">
            {isEditing ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      {selectedTemplate ? (
                        <>
                          <Settings className="w-5 h-5" />
                          Editar Plantilla
                        </>
                      ) : (
                        <>
                          <Plus className="w-5 h-5" />
                          Nueva Plantilla
                        </>
                      )}
                    </CardTitle>
                    <Button variant="ghost" size="icon" onClick={closeEditor} aria-label="Cerrar editor" data-testid="template-editor-close">
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre de la plantilla</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Mi plantilla personalizada"
                          data-testid="template-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tamaño de papel</Label>
                        <Select
                          value={formData.paper_size}
                          onValueChange={(v) => setFormData({ ...formData, paper_size: v })}
                        >
                          <SelectTrigger data-testid="paper-size-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAPER_SIZES.map(size => (
                              <SelectItem key={size.id} value={size.id}>
                                <div>
                                  <div>{size.name}</div>
                                  <div className="text-xs text-muted-foreground">{size.description}</div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Separator />

                    {/* Colors & Style */}
                    <div>
                      <h3 className="font-medium mb-4 flex items-center gap-2">
                        <Palette className="w-4 h-4" />
                        Colores y Estilo
                      </h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Color principal</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={formData.primary_color}
                              onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                              className="w-12 h-10 rounded cursor-pointer border"
                            />
                            <Input
                              value={formData.primary_color}
                              onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                              className="flex-1 font-mono"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Color secundario</Label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={formData.accent_color}
                              onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                              className="w-12 h-10 rounded cursor-pointer border"
                            />
                            <Input
                              value={formData.accent_color}
                              onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                              className="flex-1 font-mono"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Tipografía</Label>
                          <Select
                            value={formData.font_family}
                            onValueChange={(v) => setFormData({ ...formData, font_family: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FONT_FAMILIES.map(font => (
                                <SelectItem key={font.id} value={font.id}>{font.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Toggle Options */}
                    <div>
                      <h3 className="font-medium mb-4">Elementos a mostrar</h3>
                      
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                          <div className="flex items-center gap-3">
                            <Image className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <div className="font-medium text-sm">Logo del negocio</div>
                              <div className="text-xs text-muted-foreground">Muestra el logo en el encabezado</div>
                            </div>
                          </div>
                          <Switch
                            checked={formData.show_logo}
                            onCheckedChange={(v) => setFormData({ ...formData, show_logo: v })}
                          />
                        </div>
                        
                        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                          <div className="flex items-center gap-3">
                            <QrCode className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <div className="font-medium text-sm">Código QR</div>
                              <div className="text-xs text-muted-foreground">
                                {selectedType === 'invoice' ? 'QR fiscal AFIP' : 'QR de pago'}
                              </div>
                            </div>
                          </div>
                          <Switch
                            checked={formData.show_qr}
                            onCheckedChange={(v) => setFormData({ ...formData, show_qr: v })}
                          />
                        </div>

                        {selectedType === 'ticket' && (
                          <>
                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                              <div className="flex items-center gap-3">
                                <span className="text-lg">💰</span>
                                <div>
                                  <div className="font-medium text-sm">Propina sugerida</div>
                                  <div className="text-xs text-muted-foreground">Muestra opciones de propina</div>
                                </div>
                              </div>
                              <Switch
                                checked={formData.show_tip_suggestion}
                                onCheckedChange={(v) => setFormData({ ...formData, show_tip_suggestion: v })}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                              <div className="flex items-center gap-3">
                                <span className="text-lg">📱</span>
                                <div>
                                  <div className="font-medium text-sm">Redes sociales</div>
                                  <div className="text-xs text-muted-foreground">Instagram, Facebook, etc.</div>
                                </div>
                              </div>
                              <Switch
                                checked={formData.show_social}
                                onCheckedChange={(v) => setFormData({ ...formData, show_social: v })}
                              />
                            </div>
                          </>
                        )}

                        {selectedType === 'comanda' && (
                          <>
                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                              <div className="flex items-center gap-3">
                                <span className="text-lg">🔤</span>
                                <div>
                                  <div className="font-medium text-sm">Texto grande</div>
                                  <div className="text-xs text-muted-foreground">Mejor legibilidad en cocina</div>
                                </div>
                              </div>
                              <Switch
                                checked={formData.large_text}
                                onCheckedChange={(v) => setFormData({ ...formData, large_text: v })}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                              <div className="flex items-center gap-3">
                                <span className="text-lg">⚠️</span>
                                <div>
                                  <div className="font-medium text-sm">Resaltar notas</div>
                                  <div className="text-xs text-muted-foreground">Destaca alergias y modificaciones</div>
                                </div>
                              </div>
                              <Switch
                                checked={formData.highlight_notes}
                                onCheckedChange={(v) => setFormData({ ...formData, highlight_notes: v })}
                              />
                            </div>
                            
                            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border col-span-2">
                              <div className="flex items-center gap-3">
                                <span className="text-lg">🍳</span>
                                <div>
                                  <div className="font-medium text-sm">Separar por área</div>
                                  <div className="text-xs text-muted-foreground">Imprime comandas separadas para cocina y bar</div>
                                </div>
                              </div>
                              <Switch
                                checked={formData.separate_by_area}
                                onCheckedChange={(v) => setFormData({ ...formData, separate_by_area: v })}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Thanks Message */}
                    {(selectedType === 'ticket' || selectedType === 'invoice') && (
                      <div className="td-thanks space-y-6">
                        <Separator />
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <Label className="text-base">Mensaje de agradecimiento</Label>
                            <Switch
                              checked={formData.show_thanks_message}
                              onCheckedChange={(v) => setFormData({ ...formData, show_thanks_message: v })}
                            />
                          </div>
                          {formData.show_thanks_message && (
                            <Input
                              value={formData.thanks_message}
                              onChange={(e) => setFormData({ ...formData, thanks_message: e.target.value })}
                              placeholder="¡Gracias por su visita!"
                              className="max-w-md"
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Available Fields Reference */}
                    <Separator />
                    <div>
                      <h3 className="font-medium mb-3">Campos dinámicos disponibles</h3>
                      <div className="flex flex-wrap gap-2">
                        {availableFields.map(field => (
                          <Badge 
                            key={field.id} 
                            variant="outline" 
                            className="text-xs py-1"
                            title={`Ejemplo: ${field.example}`}
                          >
                            {field.label}
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Estos campos se rellenan automáticamente con los datos de cada documento.
                      </p>
                    </div>

                    {/* Actions */}
                    <Separator />
                    <div className="flex flex-wrap gap-3">
                      <Button 
                        onClick={selectedTemplate ? handleUpdate : handleCreate} 
                        data-testid="save-template-btn"
                        className="gap-2"
                      >
                        <Save className="w-4 h-4" />
                        {selectedTemplate ? 'Guardar Cambios' : 'Crear Plantilla'}
                      </Button>
                      
                      {selectedTemplate && (
                        <>
                          <Button 
                            variant="outline" 
                            onClick={() => handlePreview(selectedTemplate)}
                            disabled={generatingPreview}
                            className="gap-2"
                          >
                            {generatingPreview ? (
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                            Vista Previa
                          </Button>
                          
                          {!selectedTemplate.is_default && (
                            <Button 
                              variant="outline" 
                              onClick={() => handleSetDefault(selectedTemplate.template_id)}
                              className="gap-2"
                            >
                              <Star className="w-4 h-4" />
                              Usar por defecto
                            </Button>
                          )}
                          
                          <Button 
                            variant="destructive" 
                            onClick={() => handleDelete(selectedTemplate.template_id)}
                            className="gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Eliminar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : selectedTemplate ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {selectedTemplate.name}
                        {selectedTemplate.is_default && (
                          <Badge variant="secondary" className="text-xs">
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Default
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {PAPER_SIZES.find(p => p.id === selectedTemplate.paper_size)?.name} • {currentDocType?.name}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => handlePreview(selectedTemplate)}
                        disabled={generatingPreview}
                        className="gap-2"
                      >
                        {generatingPreview ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                        Vista Previa
                      </Button>
                      <Button onClick={() => startEdit(selectedTemplate)} className="gap-2">
                        <Settings className="w-4 h-4" />
                        Editar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-medium mb-4">Configuración</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                          <span className="text-sm text-muted-foreground">Logo</span>
                          <Badge variant={selectedTemplate.show_logo ? "default" : "secondary"}>
                            {selectedTemplate.show_logo ? <Check className="w-3 h-3 mr-1" /> : null}
                            {selectedTemplate.show_logo ? 'Sí' : 'No'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                          <span className="text-sm text-muted-foreground">Código QR</span>
                          <Badge variant={selectedTemplate.show_qr ? "default" : "secondary"}>
                            {selectedTemplate.show_qr ? <Check className="w-3 h-3 mr-1" /> : null}
                            {selectedTemplate.show_qr ? 'Sí' : 'No'}
                          </Badge>
                        </div>
                        {selectedType === 'ticket' && (
                          <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                            <span className="text-sm text-muted-foreground">Propina sugerida</span>
                            <Badge variant={selectedTemplate.show_tip_suggestion ? "default" : "secondary"}>
                              {selectedTemplate.show_tip_suggestion ? <Check className="w-3 h-3 mr-1" /> : null}
                              {selectedTemplate.show_tip_suggestion ? 'Sí' : 'No'}
                            </Badge>
                          </div>
                        )}
                        {selectedType === 'comanda' && (
                          <>
                            <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                              <span className="text-sm text-muted-foreground">Texto grande</span>
                              <Badge variant={selectedTemplate.large_text ? "default" : "secondary"}>
                                {selectedTemplate.large_text ? <Check className="w-3 h-3 mr-1" /> : null}
                                {selectedTemplate.large_text ? 'Sí' : 'No'}
                              </Badge>
                            </div>
                            <div className="flex items-center justify-between p-2 rounded bg-muted/30">
                              <span className="text-sm text-muted-foreground">Resaltar notas</span>
                              <Badge variant={selectedTemplate.highlight_notes ? "default" : "secondary"}>
                                {selectedTemplate.highlight_notes ? <Check className="w-3 h-3 mr-1" /> : null}
                                {selectedTemplate.highlight_notes ? 'Sí' : 'No'}
                              </Badge>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-medium mb-4">Estilo</h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded-lg border shadow-sm"
                              style={{ backgroundColor: selectedTemplate.primary_color }}
                            />
                            <span className="text-sm">Principal</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded-lg border shadow-sm"
                              style={{ backgroundColor: selectedTemplate.accent_color }}
                            />
                            <span className="text-sm">Secundario</span>
                          </div>
                        </div>
                        
                        {selectedTemplate.thanks_message && selectedTemplate.show_thanks_message && (
                          <div className="p-3 rounded-lg bg-muted/50 border">
                            <div className="text-xs text-muted-foreground mb-1">Mensaje</div>
                            <p className="text-sm italic">"{selectedTemplate.thanks_message}"</p>
                          </div>
                        )}
                      </div>

                      {/* Quick Actions */}
                      <div className="mt-6">
                        <h3 className="font-medium mb-3">Acciones Rápidas</h3>
                        <div className="flex flex-wrap gap-2">
                          {!selectedTemplate.is_default && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleSetDefault(selectedTemplate.template_id)}
                              className="gap-2"
                            >
                              <Star className="w-4 h-4" />
                              Establecer como default
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setShowShareDialog(true);
                            }}
                            className="gap-2"
                          >
                            <Share2 className="w-4 h-4" />
                            Compartir
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDelete(selectedTemplate.template_id)}
                            className="gap-2 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${currentDocType?.color} mx-auto mb-6 flex items-center justify-center opacity-70`}>
                    <DocTypeIcon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Diseña tus {currentDocType?.name?.toLowerCase()}s</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Personaliza la apariencia de tus documentos. Selecciona una plantilla existente o crea una nueva.
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button onClick={() => startEdit()} className="gap-2">
                      <Plus className="w-4 h-4" />
                      Crear Plantilla
                    </Button>
                    {Object.entries(presets).filter(([_, p]) => p.doc_type === selectedType).length > 0 && (
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          const firstPreset = Object.entries(presets).find(([_, p]) => p.doc_type === selectedType);
                          if (firstPreset) startFromPreset(firstPreset[0]);
                        }}
                        className="gap-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        Usar Predefinida
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Vista Previa: {selectedTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Así se verá tu documento con datos de ejemplo
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-muted/30 rounded-lg p-4">
            {previewPdf && (
              <iframe
                src={`data:application/pdf;base64,${previewPdf}`}
                className="w-full h-[60vh] rounded-lg border bg-white"
                title="Vista previa del documento"
              />
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-1">
              <Button variant="outline" onClick={handlePrint} className="gap-2">
                <Printer className="w-4 h-4" />
                Imprimir
              </Button>
              <Button variant="outline" onClick={() => setShowShareDialog(true)} className="gap-2">
                <Share2 className="w-4 h-4" />
                Compartir
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Cerrar
              </Button>
              <Button onClick={handleDownloadPdf} className="gap-2">
                <Download className="w-4 h-4" />
                Descargar PDF
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Compartir Plantilla
            </DialogTitle>
            <DialogDescription>
              Envía esta plantilla por WhatsApp, Email o copia el enlace
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-14"
              onClick={handleShareWhatsApp}
            >
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <div className="font-medium">WhatsApp</div>
                <div className="text-xs text-muted-foreground">Enviar información de la plantilla</div>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-14"
              onClick={handleShareEmail}
            >
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <div className="font-medium">Email</div>
                <div className="text-xs text-muted-foreground">Abrir cliente de correo</div>
              </div>
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 h-14"
              onClick={handleCopyLink}
            >
              <div className="w-10 h-10 rounded-full bg-gray-500 flex items-center justify-center">
                <Copy className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <div className="font-medium">Copiar Enlace</div>
                <div className="text-xs text-muted-foreground">Copiar URL al portapapeles</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowShareDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
